import React, { useState, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet-async";
import "../css/dashboard.css";
import Navbar from "../components/navbar";
import Footer from "../components/footer";

export default function Dashboard() {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Subscription States
  const [isPro, setIsPro] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelDate, setCancelDate] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [isActionPending, setIsActionPending] = useState(false);

  // Modals
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    resumeId: null,
  });
  const [renameModal, setRenameModal] = useState({
    show: false,
    resumeId: null,
    newTitle: "",
  });
  const [forceDeleteModal, setForceDeleteModal] = useState(false);

  // NEW: Pro Paywall Modal State
  const [showProModal, setShowProModal] = useState(false);

  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));
  const userName = user?.name ? user.name.split(" ")[0] : "Guest";

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  useEffect(() => {
    fetchStatus();
    fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/status`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setIsPro(data.isPro);
        setIsCanceling(data.isCanceling);
        setCancelDate(data.cancelDate);
      }
    } catch (err) {
      console.error("Failed to fetch user status", err);
    }
  };

  const fetchResumes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/resumes/list`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();

        if (data.resumes && data.resumes.length > 0) {
          const formattedResumes = data.resumes.map((doc) => {
            // FORCES DD/MM/YYYY FORMAT
            const formattedDate = new Date(doc.last_updated).toLocaleDateString(
              "en-GB",
            );

            let parsedData = {};
            try {
              parsedData =
                typeof doc.resume_data === "string"
                  ? JSON.parse(doc.resume_data)
                  : doc.resume_data || {};
            } catch (e) {
              console.error("Failed to parse CV data for doc ID:", doc.id);
            }

            const expCount = parsedData?.experience?.length || 0;
            const skillsArray = Array.isArray(parsedData?.skills)
              ? parsedData.skills
              : [];
            const skillCount = skillsArray.filter(
              (s) => s && s.trim() !== "",
            ).length;

            return {
              id: doc.id,
              title: doc.title || "My CV",
              targetRole: parsedData?.personal?.role || "Unspecified Role",
              lastEdited: formattedDate,
              rawDate: new Date(doc.last_updated).getTime(),
              atsScore: doc.ats_score || 0,
              template: parsedData?.design?.theme || "Modern",
              rawFormData: parsedData,
              expCount,
              skillCount,
            };
          });

          setResumes(formattedResumes);
        } else {
          setResumes([]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch CVs", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && !isPro && resumes.length > 3) {
      setForceDeleteModal(true);
    } else {
      setForceDeleteModal(false);
    }
  }, [isPro, resumes.length, isLoading]);

  const handleCreateNew = () => {
    if (!isPro && resumes.length >= 3) {
      toast.error(
        "Free plan limit reached (Max 3 CVs). Upgrade to Pro for unlimited CVs!",
        { duration: 4000, icon: "🔒" },
      );
      return;
    }

    localStorage.removeItem("resumeDraft");
    localStorage.removeItem("designDraft");
    navigate("/builder", { state: { isNew: true } });
  };

  const handleEditResume = (resumeData) => {
    localStorage.removeItem("resumeDraft");
    localStorage.removeItem("designDraft");

    navigate("/builder", {
      state: {
        isEdit: true,
        resumeId: resumeData.id,
        title: resumeData.title,
        resumeData: resumeData.rawFormData,
      },
    });
  };

  const initiateDelete = (id, e) => {
    e.stopPropagation();
    setDeleteModal({ show: true, resumeId: id });
  };

  const confirmDelete = async () => {
    const targetId = deleteModal.resumeId;
    const previousResumes = [...resumes];

    setResumes(resumes.filter((r) => r.id !== targetId));
    setDeleteModal({ show: false, resumeId: null });

    try {
      const res = await fetch(`${API_URL}/api/resumes/delete/${targetId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Server failed to delete");
      toast.success("CV deleted");
    } catch (error) {
      console.error("Failed to delete", error);
      setResumes(previousResumes);
      toast.error(error.message || "Network error. CV restored.");
    }
  };

  const handleForceDelete = async (targetId) => {
    const previousResumes = [...resumes];
    setResumes(resumes.filter((r) => r.id !== targetId));

    try {
      const res = await fetch(`${API_URL}/api/resumes/delete/${targetId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Server failed to delete");
      toast.success("CV deleted");
    } catch (error) {
      console.error("Failed to delete", error);
      setResumes(previousResumes);
      toast.error(error.message || "Network error. CV restored.");
    }
  };

  const confirmRename = async () => {
    if (!renameModal.newTitle.trim()) return;

    const targetId = renameModal.resumeId;
    const newTitle = renameModal.newTitle.trim();
    const targetResume = resumes.find((r) => r.id === targetId);
    const previousResumes = [...resumes];

    setResumes(
      resumes.map((r) => (r.id === targetId ? { ...r, title: newTitle } : r)),
    );
    setRenameModal({ show: false, resumeId: null, newTitle: "" });

    try {
      const res = await fetch(`${API_URL}/api/resumes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resumeId: targetResume.id,
          formData: targetResume.rawFormData,
          design: targetResume.rawFormData.design,
          title: newTitle,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Rename failed");
      toast.success("CV renamed");
    } catch (error) {
      console.error("Failed to rename CV", error);
      setResumes(previousResumes);
      toast.error(error.message || "Network error. Name reverted.");
    }
  };

  const handleDuplicate = async (resume, e) => {
    e.stopPropagation();

    if (!isPro && resumes.length >= 3) {
      toast.error(
        "Free plan limit reached (Max 3 CVs). Upgrade to Pro to duplicate!",
        { duration: 4000, icon: "🔒" },
      );
      return;
    }

    if (isActionPending) return;
    setIsActionPending(true);

    const toastId = toast.loading("Duplicating...");

    try {
      const duplicatedData = {
        formData: resume.rawFormData,
        title: `${resume.title} (Copy)`,
      };

      const res = await fetch(`${API_URL}/api/resumes/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(duplicatedData),
      });

      const data = await res.json();

      if (res.ok) {
        await fetchResumes();
        toast.success("Duplicate created!", { id: toastId });
      } else {
        throw new Error(data.message || "Failed to duplicate");
      }
    } catch (error) {
      console.error("Failed to duplicate", error);
      toast.error(error.message || "Failed to duplicate.", { id: toastId });
    } finally {
      setIsActionPending(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "score-high";
    if (score >= 50) return "score-med";
    return "score-low";
  };

  const averageAts =
    resumes.length > 0
      ? Math.round(
          resumes.reduce((acc, curr) => acc + curr.atsScore, 0) /
            resumes.length,
        )
      : 0;

  const uniqueRolesCount = new Set(
    resumes.map((r) => r.targetRole.toLowerCase()),
  ).size;
  const uniqueTemplatesCount = new Set(resumes.map((r) => r.template)).size;

  const skillCounts = {};
  resumes.forEach((r) => {
    if (Array.isArray(r.rawFormData?.skills)) {
      r.rawFormData.skills.forEach((s) => {
        if (s && s.trim()) {
          const cleanSkill = s.trim();
          skillCounts[cleanSkill] = (skillCounts[cleanSkill] || 0) + 1;
        }
      });
    }
  });
  const topSkills = Object.entries(skillCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map((entry) => entry[0]);

  const getSmartInsight = () => {
    if (resumes.length === 0) {
      return "Create your first CV to unlock personalised Applicant Tracking System (ATS) insights.";
    }
    if (averageAts < 60) {
      return "Your average score needs a boost. ATS algorithms heavily favour hard metrics. Review your experience sections and add numbers (e.g., 'Increased efficiency by 15%') to quantify your achievements.";
    }
    if (averageAts < 80) {
      return "You're on the right track! To break into the 80%+ tier, ensure your 'Target Job Title' matches the exact title on the job posting, and weave specific industry keywords into your summary.";
    }
    return "Outstanding! Your CVs are highly optimised for recruitment software. To maintain this high standard, ensure you slightly tailor duplicates for every unique job application rather than using one generic file.";
  };

  const processedResumes = resumes
    .filter((resume) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        resume.title.toLowerCase().includes(searchLower) ||
        resume.targetRole.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (sortBy === "newest") return b.rawDate - a.rawDate;
      if (sortBy === "score") return b.atsScore - a.atsScore;
      if (sortBy === "alpha") return a.title.localeCompare(b.title);
      return 0;
    });

  if (isLoading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h2>Loading your workspace...</h2>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Resumify | Dashboard</title>
        <meta
          name="description"
          content="Manage your CVs, track job applications, and analyze your ATS scores. Access your personalised Resumify workspace."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Toaster position="bottom-right" />
      <Navbar />
      <div className="dashboard-layout">
        <main className="dashboard-main">
          <header className="main-header">
            <div className="header-greeting">
              <h1>Welcome back, {userName}!</h1>
              <p>
                You have {resumes.length} optimised documents ready for
                applications.
              </p>
            </div>
          </header>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div className="stat-card" style={{ padding: "20px" }}>
              <span className="stat-label">Total CVs</span>
              <strong className="stat-value">{resumes.length}</strong>
            </div>
            <div className="stat-card" style={{ padding: "20px" }}>
              <span className="stat-label">Average Score</span>
              <strong
                className={`stat-value text-gradient ${getScoreColor(averageAts)}`}
              >
                {resumes.length > 0 ? `${averageAts}%` : "-"}
              </strong>
            </div>
            <div className="stat-card" style={{ padding: "20px" }}>
              <span className="stat-label">Target Roles</span>
              <strong className="stat-value" style={{ color: "#3b82f6" }}>
                {uniqueRolesCount}
              </strong>
            </div>
            <div className="stat-card" style={{ padding: "20px" }}>
              <span className="stat-label">Designs Used</span>
              <strong className="stat-value" style={{ color: "#8b5cf6" }}>
                {uniqueTemplatesCount}
              </strong>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "40px",
              flexWrap: "wrap",
            }}
          >
            <div
              className="stat-card insight-card"
              style={{
                flex: "2",
                minWidth: "300px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderLeft: "4px solid #4f46e5",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <span style={{ fontSize: "1.2rem" }}>💡</span>
                <span className="stat-label" style={{ margin: 0 }}>
                  Actionable Insight
                </span>
              </div>
              <p
                style={{
                  fontSize: "0.95rem",
                  color: "#334155",
                  lineHeight: "1.6",
                  fontWeight: 500,
                }}
              >
                {getSmartInsight()}
              </p>
            </div>

            <div
              className="stat-card"
              style={{
                flex: "1",
                minWidth: "250px",
                background: "white",
                border: "1px solid #e2e8f0",
              }}
            >
              <span className="stat-label">🌟 Your Top Skills</span>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "12px",
                }}
              >
                {topSkills.length > 0 ? (
                  topSkills.map((skill, i) => (
                    <span
                      key={i}
                      style={{
                        background: "#f1f5f9",
                        color: "#0f172a",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <p style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                    No skills extracted yet.
                  </p>
                )}
              </div>
            </div>
          </div>

          <section className="resumes-section">
            <div
              className="section-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <h2>Your Documents</h2>
              <div
                className="filter-controls"
                style={{ display: "flex", gap: "10px" }}
              >
                <input
                  type="search"
                  placeholder="Search CVs..."
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                  }}
                />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "white",
                    color: "#475569",
                    cursor: "pointer",
                  }}
                >
                  <option value="newest">Sort by Newest</option>
                  <option value="score">Sort by ATS Score</option>
                  <option value="alpha">Sort Alphabetically</option>
                </select>
              </div>
            </div>

            <div className="resume-grid">
              <div
                className="resume-card new-card"
                onClick={handleCreateNew}
                style={{ cursor: "pointer" }}
              >
                <div className="new-card-content">
                  <span className="plus-huge text-gradient">+</span>
                  <p>Create New CV</p>
                  <span className="new-card-sub">Start from scratch</span>
                </div>
              </div>

              {processedResumes.map((resume) => (
                <div
                  key={resume.id}
                  className="resume-card"
                  onClick={() => handleEditResume(resume)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-header">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <h3 style={{ margin: 0 }}>{resume.title}</h3>
                      <button
                        className="icon-btn"
                        style={{
                          width: "28px",
                          height: "28px",
                          fontSize: "0.8rem",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameModal({
                            show: true,
                            resumeId: resume.id,
                            newTitle: resume.title,
                          });
                        }}
                        title="Rename CV"
                      >
                        🖊️
                      </button>
                    </div>
                    <span className="role-badge" style={{ marginTop: "8px" }}>
                      {resume.targetRole}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="resume-preview-box">
                      <div className="skeleton-line full"></div>
                      <div className="skeleton-line medium"></div>
                      <div className="skeleton-line full"></div>
                    </div>

                    <div
                      className="resume-meta"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        flexGrow: 1,
                      }}
                    >
                      <div>
                        <strong style={{ color: "#334155" }}>
                          Last Edited:
                        </strong>{" "}
                        <br />
                        {resume.lastEdited}
                      </div>
                      <div style={{ display: "flex", gap: "15px" }}>
                        <div>
                          <strong style={{ color: "#334155" }}>Roles:</strong>{" "}
                          <br />
                          {resume.expCount}
                        </div>
                        <div>
                          <strong style={{ color: "#334155" }}>Skills:</strong>{" "}
                          <br />
                          {resume.skillCount}
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: "#334155" }}>Template:</strong>{" "}
                        <br />
                        <span
                          style={{
                            textTransform: "capitalize",
                            background: "#f1f5f9",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "0.8rem",
                          }}
                        >
                          {resume.template}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="card-footer"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className={`ats-score-badge ${getScoreColor(resume.atsScore)}`}
                    >
                      <span className="score-val">{resume.atsScore}%</span>
                      <span className="score-label">ATS Score</span>
                    </div>

                    <div
                      className="action-buttons"
                      style={{ display: "flex", gap: "6px" }}
                    >
                      <button
                        className="icon-btn"
                        title="Edit Content"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditResume(resume);
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="icon-btn"
                        title="Duplicate"
                        onClick={(e) => handleDuplicate(resume, e)}
                      >
                        📋
                      </button>
                      <button
                        className="icon-btn"
                        title="Download PDF"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditResume(resume);
                        }}
                      >
                        ⬇
                      </button>
                      <button
                        className="icon-btn"
                        title="Delete"
                        onClick={(e) => initiateDelete(resume.id, e)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* PRO SUBSCRIPTION POPUP MODAL */}
      {showProModal && (
        <div
          className="custom-modal-overlay"
          onClick={() => setShowProModal(false)}
          style={{ zIndex: 1000 }}
        >
          <div
            className="custom-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-icon-danger"
              style={{ fontSize: "3rem", marginBottom: "10px" }}
            >
              💎
            </div>
            <h3>Pro Feature</h3>
            <p>
              The AI Interview Simulator is an exclusive feature for Pro users.
              Upgrade your account to practice tailored questions and get
              real-time feedback.
            </p>
            <div
              className="custom-modal-actions"
              style={{ flexDirection: "column", gap: "10px" }}
            >
              <button
                className="modal-btn"
                style={{ background: "#4f46e5", color: "white" }}
                onClick={() => navigate("/pricing")}
              >
                Upgrade to Pro
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setShowProModal(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RENAME MODAL */}
      {renameModal.show && (
        <div
          className="custom-modal-overlay"
          onClick={() =>
            setRenameModal({ show: false, resumeId: null, newTitle: "" })
          }
          style={{ zIndex: 1000 }}
        >
          <div
            className="custom-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: "left" }}
          >
            <h3 style={{ marginBottom: "10px", fontSize: "1.3rem" }}>
              Rename CV
            </h3>
            <p
              style={{
                color: "#64748b",
                marginBottom: "20px",
                fontSize: "0.95rem",
              }}
            >
              Give this CV a descriptive name so you can easily identify it
              later.
            </p>
            <input
              type="text"
              value={renameModal.newTitle}
              onChange={(e) =>
                setRenameModal({ ...renameModal, newTitle: e.target.value })
              }
              autoFocus
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                fontSize: "1rem",
                marginBottom: "24px",
                boxSizing: "border-box",
              }}
            />
            <div className="custom-modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() =>
                  setRenameModal({ show: false, resumeId: null, newTitle: "" })
                }
              >
                Cancel
              </button>
              <button
                className="modal-btn"
                style={{ background: "#2563eb", color: "white" }}
                onClick={confirmRename}
                disabled={!renameModal.newTitle.trim()}
              >
                Save Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteModal.show && (
        <div
          className="custom-modal-overlay"
          onClick={() => setDeleteModal({ show: false, resumeId: null })}
          style={{ zIndex: 1000 }}
        >
          <div
            className="custom-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-icon-danger">⚠️</div>
            <h3>Delete CV?</h3>
            <p>
              Are you sure you want to delete this CV? This action is permanent
              and cannot be undone.
            </p>
            <div className="custom-modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setDeleteModal({ show: false, resumeId: null })}
              >
                Cancel
              </button>
              <button className="modal-btn delete" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FORCE DELETE MODAL (Plan Downgrade / Limit Exceeded) */}
      {forceDeleteModal && (
        <div
          className="modal-overlay fade-in"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.8)",
            backdropFilter: "blur(6px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Ensure it sits on top of everything
          }}
        >
          <div
            className="modal-content custom-scrollbar"
            style={{
              background: "white",
              padding: "32px",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "500px",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "3rem", marginBottom: "12px" }}>⚠️</div>
              <h2 style={{ color: "#0f172a", margin: "0 0 8px 0" }}>
                Storage Limit Exceeded
              </h2>
              <p style={{ color: "#475569", lineHeight: "1.5", margin: 0 }}>
                The free plan allows a maximum of <strong>3</strong> CVs. You
                currently have <strong>{resumes.length}</strong>.
                <br />
                <br />
                Please delete <strong>{resumes.length - 3}</strong> CV(s) to
                regain access to your dashboard.
              </p>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {resumes.map((resume) => (
                <div
                  key={resume.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    background: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div style={{ overflow: "hidden", paddingRight: "15px" }}>
                    <h4
                      style={{
                        margin: "0 0 4px 0",
                        color: "#1e293b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {resume.title}
                    </h4>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
                      Last edited: {resume.lastEdited}
                    </span>
                  </div>
                  <button
                    onClick={() => handleForceDelete(resume.id)}
                    style={{
                      background: "#fee2e2",
                      color: "#ef4444",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      transition: "all 0.2s",
                      minWidth: "80px",
                    }}
                    onMouseOver={(e) => (e.target.style.background = "#fecaca")}
                    onMouseOut={(e) => (e.target.style.background = "#fee2e2")}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}
